import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DialogueModal from './DialogModal';

export interface ExportLimitWarningDialogProps {
  isOpen: boolean;
  limit: number;
  handleClose: () => void;
  handleConfirm: () => void;

  /**
   * Reusable props
   */
  
  title?: string;
  primaryMessage?: string;
  secondaryMessage?: string;
  cancelButtonText?: string;
  confirmButtonText?: string;
  dialogWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  headerColor?: string;
  primaryColor?: string;
  warningColor?: string;
  onAfterConfirm?: () => void;
}

const ExportLimitWarningDialog: React.FC<ExportLimitWarningDialogProps> = ({
  isOpen,
  limit,
  handleClose,
  handleConfirm,

  title = 'Export Records Overlimit',
  primaryMessage,
  secondaryMessage,
  cancelButtonText = 'Cancel',
  confirmButtonText = 'Export',
  dialogWidth = 'sm',
  headerColor = '#003771',
  primaryColor = '#39ACE7',
  warningColor = '#FCC046',
  onAfterConfirm,
}) => {
  const resolvedPrimaryMessage =
    primaryMessage ?? `Export functionality allows max. ${limit} records at a time.`;

  const resolvedSecondaryMessage =
    secondaryMessage ?? `If you continue, only ${limit} records will be exported.`;

  const handleExport = () => {
    handleConfirm();
    onAfterConfirm?.();
    handleClose();
  };

  return (
    <DialogueModal
      isOpen={isOpen}
      handleClose={handleClose}
      title={title}
      width={dialogWidth}
      titleSx={{
        bgcolor: `${headerColor} !important`,
        color: '#ffffff !important',
        px: '20px !important',
        py: '13px !important',
        fontWeight: 700,
        fontSize: '0.95rem',
        lineHeight: 1.2,
        letterSpacing: '0.2px',
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2.5,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2.5,
          bgcolor: '#ffffff',
        }}
      >
        <WarningAmberRoundedIcon
          sx={{
            color: warningColor,
            fontSize: '4.35rem',
            flexShrink: 0,
            mt: -0.3,
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.7,
            textAlign: 'left',
            pt: 0.5,
            minWidth: 0,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: '#111827',
              fontSize: '1.18rem',
              fontWeight: 500,
              lineHeight: 1.35,
            }}
          >
            {resolvedPrimaryMessage}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#4B5563',
              fontSize: '0.92rem',
              fontWeight: 400,
              lineHeight: 1.4,
            }}
          >
            {resolvedSecondaryMessage}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          px: 3,
          pb: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 2.5,
          bgcolor: '#ffffff',
        }}
      >
        <Button
          onClick={handleClose}
          variant="text"
          disableElevation
          sx={{
            color: `${primaryColor} !important`,
            bgcolor: 'transparent !important',
            minWidth: 72,
            height: 36,
            px: 2,
            fontSize: '0.78rem',
            fontWeight: 700,
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
            borderRadius: '4px',
            '&:hover': {
              bgcolor: 'rgba(57, 172, 231, 0.08) !important',
            },
          }}
        >
          {cancelButtonText}
        </Button>

        <Button
          onClick={handleExport}
          variant="contained"
          disableElevation
          sx={{
            bgcolor: `${primaryColor} !important`,
            color: '#ffffff !important',
            minWidth: 112,
            height: 36,
            px: 3,
            fontSize: '0.78rem',
            fontWeight: 700,
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
            borderRadius: '4px',
            boxShadow: 'none !important',
            '&:hover': {
              bgcolor: `${primaryColor} !important`,
              opacity: 0.9,
              boxShadow: 'none !important',
            },
            '&:focus': {
              boxShadow: 'none !important',
            },
          }}
        >
          {confirmButtonText}
        </Button>
      </Box>
    </DialogueModal>
  );
};

export default ExportLimitWarningDialog;